/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getPublicSiteUrl } from "@cosmos/common-utils";
import { isError } from "@epic-mw/error-types";
import validator from "validator";

import type { ErrorLogRequest, ErrorLogResponse } from "../types/error-logging.types";

export async function reportClientError(error: Error & { digest?: string }): Promise<void> {
	try {
		// Collect all error properties as stringified values
		const errorProperties: Record<string, string> = {};
		Object.getOwnPropertyNames(error).forEach((key) => {
			try {
				errorProperties[key] = JSON.stringify(error[key as keyof typeof error]);
			} catch {
				errorProperties[key] = String(error[key as keyof typeof error]);
			}
		});

		const errorData: ErrorLogRequest = {
			message: error.message?.substring(0, 5000) || "Unknown client error",
			stack: error.stack?.substring(0, 10000),
			url: typeof window !== "undefined" ? window.location.href : undefined,
			userAgent: typeof window !== "undefined" ? window.navigator.userAgent : undefined,
			timestamp: new Date().toISOString(),
			errorProperties,
		};

		// Uses /api/cosmos (not /api) — this is the cosmos-specific error logging endpoint.
		// getPublicSiteUrl() includes the full origin + base path (e.g., "https://www.fab.com/o").
		const apiPath = `${getPublicSiteUrl()}${process.env.NEXT_PUBLIC_COSMOS_API_PATH || "/api/cosmos"}`;

		const response = await fetch(`${apiPath}/error-logging`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(errorData),
		});

		if (!response.ok) {
			console.warn("Failed to report client error to server:", response.status, response.statusText);
		} else {
			const result: ErrorLogResponse = await response.json();
			if (!result.success) {
				console.warn("Error reporting failed:", result.error);
			}
		}
	} catch (reportingError) {
		console.warn("Failed to report client error:", reportingError);
	}
}

export async function reportClientErrorAsync(error: Error & { digest?: string }): Promise<void> {
	// Use setTimeout to make this truly async and non-blocking
	void setTimeout(() => {
		void (async () => {
			try {
				await reportClientError(error);
			} catch (err: unknown) {
				console.warn("Async error reporting failed:", isError(err) && err.message);
			}
		})();
	}, 0);
}

// Enhanced function to manually report errors with additional context
export function reportError(
	error: Error | string,
	context?: {
		component?: string;
		action?: string;
		userId?: string;
		additionalData?: Record<string, unknown>;
	},
): void {
	const errorObj = typeof error === "string" ? new Error(error) : error;

	// Add context to error stack if provided
	if (context) {
		const contextString = Object.entries(context)
			.map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
			.join(", ");
		const safeContextString = validator.escape(contextString.substring(0, 1000));
		errorObj.stack = `${errorObj.stack}\n\nContext: ${safeContextString}`;
	}

	reportClientErrorAsync(errorObj).catch((err: Error) => {
		console.warn("Async error reporting failed:", isError(err) && err.message);
	});
}
